import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button } from 'antd';

import { DataState, Skeleton, deriveDataState } from '../loading';
import LazyItemsMap from '../map/LazyItemsMap';
import ItemsMapList from './ItemsMapList';
import { RecordTitle } from '../common/RecordBadge';
import { MAP_TILES } from '../../config/mapTiles';
import { DEFAULT_THEME, buildPalette } from '../../config/themes';
import { useOptionalTheme } from '../../contexts/ThemeContext';
import { placeItems } from '../../utils/itemLocations';
import { getItemDisplayValue } from '../../utils/items';
import { normalizeId } from '../../utils/ids';
import { rowSourceOf } from '../../utils/linkedRecords';
import { rowSourceIdentity } from '../../utils/recordQualifier';
import { prefersReducedMotion } from '../tour/targets';
import { RATE_LIMITED_TEXT } from '../../utils/feedMessages';
import { MAP_MAX_ITEMS } from '../../hooks/useMapItems';

const number = n => Number(n || 0).toLocaleString('en-US');

const MAP_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ', '+', '-', '=', 'PageUp', 'PageDown', 'Home', 'End']);
const CONTROL = 'button, a[href], [role="button"], input, select, textarea, summary';

/** Stop the keys a focused control or the focused map acts on from reaching the page's list keys. */
function keepKeysHere(event) {
	const target = event.target;
	if (!(target instanceof Element)) return;
	const onMap = Boolean(target.closest('.items-map__frame'));
	const onControl = Boolean(target.closest(CONTROL));
	if (onMap && MAP_KEYS.has(event.key)) event.stopPropagation();
	else if (onControl && (event.key === 'Enter' || event.key === ' ')) event.stopPropagation();
}
const items = n => `${number(n)} ${Number(n) === 1 ? 'item' : 'items'}`;

/**
 * What the map says about the items it was given, in one or two sentences
 * (KPR-392: "says how many have none"). Counted over what is loaded; when
 * the list holds more, the second sentence says how much of it that is.
 */
export function mapSummary({ placed, unplaced, loaded, total, max = MAP_MAX_ITEMS }) {
	const parts = [];
	if (!loaded) return '';
	if (!placed) parts.push(loaded === 1 ? 'This item has no location.' : `None of these ${items(loaded)} has a location.`);
	else if (!unplaced) parts.push(`${items(placed)} on the map.`);
	else parts.push(`${number(placed)} of ${items(loaded)} on the map. ${number(unplaced)} ${unplaced === 1 ? 'has' : 'have'} no location.`);
	if (total > loaded) {
		parts.push(loaded >= max
			? `The map holds the first ${number(max)} of ${number(total)}. Narrow the filter to see the rest.`
			: `Showing the first ${number(loaded)} of ${number(total)}.`);
	}
	return parts.join(' ');
}

/**
 * The list's MAP view (KPR-392): the items of the list's own question, placed
 * by their location elements, with the keyboard list beside the map. The map
 * itself is the lazy map chunk (components/map/LazyItemsMap); everything here
 * is small and in the main bundle.
 *
 *   <ItemsMapView
 *     map={useMapItems(...)}              // the items, their total and the read's state
 *     placement={placeItems(...)}         // optional: where each goes, when the page has it
 *     getElementsForCard cardsById resolveLookupValue collection
 *     selectedId onSelectItem(item)       // the page's open item
 *     expandedItemId renderExpanded       // a phone: the open row grows into the detail
 *     isMobile
 *   />
 *
 * A pin or a row opens its item the way a tile does (the page's `?item=`).
 * The map is never the only way to an item: whatever it cannot do — the
 * chunk did not arrive, the browser has no WebGL, the tile file failed — the
 * list keeps every item reachable and says what happened in a line.
 */
export default function ItemsMapView({
	map,
	placement = null,
	getElementsForCard,
	cardsById,
	resolveLookupValue,
	collection = null,
	selectedId = null,
	onSelectItem,
	expandedItemId = null,
	renderExpanded,
	isMobile = false,
	config = MAP_TILES,
}) {
	const themeCtx = useOptionalTheme();
	const mode = themeCtx?.mode === 'dark' ? 'dark' : 'light';
	const palette = useMemo(
		() => themeCtx?.palette || buildPalette(themeCtx?.theme || DEFAULT_THEME, mode),
		[themeCtx?.palette, themeCtx?.theme, mode]
	);

	const resolveElements = useCallback(
		cardId => (typeof getElementsForCard === 'function' ? getElementsForCard(cardId) || [] : []),
		[getElementsForCard]
	);
	const computed = useMemo(
		() => (placement ? null : placeItems(map.items, resolveElements)),
		[placement, map.items, resolveElements]
	);
	const { placed, unplaced } = placement || computed;

	// The row's title is the item's own (chip + title), qualified where the
	// row comes from another collection, as in the table.
	const titleOf = useCallback((item) => {
		const card = cardsById?.[normalizeId(item?.card_id)] || null;
		const source = rowSourceOf(item, collection);
		return (
			<RecordTitle
				kind="item"
				record={card}
				size="sm"
				title={getItemDisplayValue({ card, elementDefs: resolveElements(item?.card_id), item, resolveLookupValue })}
				collection={source ? rowSourceIdentity(source) : null}
				collectionRelation={source ? source.via : null}
			/>
		);
	}, [cardsById, collection, resolveElements, resolveLookupValue]);

	const rows = useMemo(
		() => placed.map(point => ({ id: point.id, item: point.item, title: titleOf(point.item), subtitle: point.address })),
		[placed, titleOf]
	);
	const unplacedRows = useMemo(
		() => unplaced.map(item => ({ id: normalizeId(item?._id), item, title: titleOf(item) })),
		[unplaced, titleOf]
	);
	// The canvas takes the points alone: id and place, nothing else of an item.
	const points = useMemo(() => placed.map(({ id, lat, lng }) => ({ id, lat, lng })), [placed]);

	const [focusedId, setFocusedId] = useState(null);
	const [basemapFailed, setBasemapFailed] = useState(false);
	const [unavailable, setUnavailable] = useState(false);

	const openById = useCallback((id) => {
		const point = placed.find(entry => entry.id === id);
		if (point) onSelectItem?.(point.item);
	}, [placed, onSelectItem]);

	const loaded = map.items.length;
	const summary = mapSummary({ placed: placed.length, unplaced: unplaced.length, loaded, total: map.total });
	// A new question fits the map to its items again; so does its first
	// placed item arriving. Another page of the same question does not.
	const fitKey = `${map.questionKey}|${placed.length ? 'some' : 'none'}`;
	const mapLabel = placed.length ? `Map of ${items(placed.length)}` : 'Map';

	const hasData = loaded > 0;
	const firstLoadFailed = Boolean(map.error) && !hasData;
	const state = deriveDataState({
		loading: map.loading && !hasData,
		error: firstLoadFailed ? map.error : null,
		hasData,
		isEmpty: !map.loading && !map.error && !hasData,
	});
	const errorText = map.error?.kind === 'rate_limited'
		? RATE_LIMITED_TEXT
		: map.error?.kind === 'refused'
			? 'This filter could not be run. Change it above.'
			: null;

	const frame = config ? (
		<div className="items-map__frame">
			{unavailable ? (
				<div className="items-map__frame-note" role="status">
					This browser cannot draw the map. The list holds every item.
				</div>
			) : (
				<LazyItemsMap
					waiting={<div className="items-map__waiting" aria-busy="true"><Skeleton variant="block" height="100%" /></div>}
					renderFailure={retry => (
						<div className="items-map__frame-note" role="status">
							<span>The map could not be loaded. The list holds every item.</span>
							<Button size="small" onClick={retry}>Retry</Button>
						</div>
					)}
					config={config}
					mode={mode}
					palette={palette}
					placed={points}
					selectedId={selectedId}
					focusedId={focusedId}
					fitKey={fitKey}
					onSelect={openById}
					onBasemapError={() => setBasemapFailed(true)}
					onUnavailable={() => setUnavailable(true)}
					reducedMotion={prefersReducedMotion()}
					cooperative={isMobile}
					label={mapLabel}
				/>
			)}
		</div>
	) : null;

	const partialFailure = map.error && hasData ? (
		<Alert
			type="warning"
			showIcon
			className="items-map__alert"
			message={map.error.kind === 'rate_limited'
				? `${RATE_LIMITED_TEXT} The map shows the first ${items(loaded)}.`
				: `The map stopped after the first ${items(loaded)}. The rest could not be loaded.`}
			action={map.error.kind === 'refused' ? null : <Button size="small" onClick={map.retry}>Retry</Button>}
		/>
	) : null;

	const list = (
		<>
			<ItemsMapList
				rows={rows}
				unplacedRows={unplacedRows}
				selectedId={selectedId}
				onOpen={onSelectItem}
				onFocusItem={setFocusedId}
				expandedItemId={expandedItemId}
				renderExpanded={renderExpanded}
				unplacedOpenByDefault={!placed.length}
			/>
			{map.hasMore ? (
				<div className="items-map__more">
					<Button onClick={map.loadMore} loading={map.loadingMore}>Show more on the map</Button>
				</div>
			) : map.loadingMore ? (
				<div className="items-map__more" aria-busy="true"><Skeleton variant="list-row" count={2} /></div>
			) : null}
		</>
	);

	// The list's states are the list's (§14); the map beside it stays mounted
	// through them, so a new filter does not rebuild the map — its pins go,
	// and come back when the new answer lands.
	const side = (
		<div className="items-map__side">
			<DataState
				state={state}
				hasData={hasData}
				isEmpty={state === 'empty'}
				skeleton={<Skeleton variant="list-row" count={6} />}
				empty={<div className="items-map__empty">No items match</div>}
				error={errorText}
				onRetry={map.error?.kind === 'refused' ? undefined : map.retry}
				label="the map's items"
			>
				{list}
			</DataState>
		</div>
	);

	return (
		// The keys that act on what is focused here stay here: Enter or Space
		// on a button of the view (No location, Show more, Retry, the map's
		// zoom buttons) presses it, and the arrows on the focused map move
		// the map. The page's list keys (↑↓ move the open item, Enter opens
		// its page) would otherwise act as well. Esc, N and / pass through.
		// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
		<section className={`items-map${isMobile ? ' items-map--phone' : ''}`} aria-label="Map view" onKeyDown={keepKeysHere}>
			<p className="items-map__summary" role="status">{hasData && !map.loading ? summary : ''}</p>
			{basemapFailed ? (
				<Alert
					type="info"
					showIcon
					className="items-map__alert"
					message="The map's background could not be loaded. The pins are still shown."
				/>
			) : null}
			{partialFailure}
			<div className="items-map__body">
				{isMobile ? <>{frame}{side}</> : <>{side}{frame}</>}
			</div>
		</section>
	);
}
